import { DefaultValues } from 'react-hook-form';
import * as yup from 'yup';

import { RangeFilterOrNull, SpatialFiltersInput } from '@hcs/types';
import {
  getRangeMaxYup,
  GetRangeMaxYupOptions,
  getRangeMinYup,
  GetRangeMinYupOptions,
} from '@hcs/utils';
import { getMaxFromRangeFilter, getMinFromRangeFilter } from '@hcs/utils';

import { LEAD_FEED_PROPERTY_TYPE } from '../constants/buyBox.constants';
import {
  GeoBoundaryCity,
  GeoBoundaryZipcode,
} from '../features/BuyBoxInputs/GeoBoundary';
import {
  BulkEditBuyBoxFormData,
  BulkEditPatch,
  BulkEditRangeActiveFormData,
  BulkEditRangeFilter,
  BulkEditRangeFilterOrNull,
  BulkEditRangeFilters,
  BuyBox,
  BuyBoxFormData,
  BuyBoxListItem,
  BuyBoxPatch,
  BuyBoxRangeFormData,
  ExcludeFilterOrNull,
  Filters,
  LeadFeedPropertyType,
  NewBuyBox,
  RangeFilters,
  SetFilterOrNull,
  Template,
} from '../types';

import {
  getBuyBoxFiltersFromSpatial,
  getIsExcludedFromexcludeFilter,
} from './api.utils';

const getBuyBoxRangeFilter = (
  minValue: number | null,
  maxValue: number | null,
): RangeFilterOrNull => {
  if (minValue == null && maxValue == null) {
    return null;
  }
  return {
    rng: [minValue ?? 0, maxValue ?? 99999999],
  };
};

/**
 * Validates the parameter as a whole number with an optional .5 increment
 *
 * ex. (1 OR 1.5) NOT (1.2 OR 1.55)
 */
const wholeNumberHalfIncrement = (val: number | null | undefined) => {
  if (val === null || val === undefined || val.toString().length === 0)
    return true;

  const regex = /^\d*(\.)?[5]?$/;
  return regex.test(val.toString());
};

export const getNameYup = () => {
  return yup.string().trim().required('Name is required').max(255);
};

export const getMsaYup = () => {
  return yup
    .string()
    .nullable()
    .test('msa-validation', 'MSA is required', function (value) {
      const { geoBoundary } = this.parent;
      const hasMsaId = value && value.trim().length > 0;

      if (geoBoundary === 'MSA' && !hasMsaId) {
        return this.createError({
          message: 'This field is required',
        });
      }

      return true;
    });
};

// only the bulk fields use the active fields
type BuyBoxMinRangeOptions = GetRangeMinYupOptions<
  keyof BulkEditRangeActiveFormData
>;
type BuyBoxMaxRangeOptions = GetRangeMaxYupOptions<
  keyof BuyBoxRangeFormData,
  keyof BulkEditRangeActiveFormData
>;

export const getMinListingPriceYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxListingPriceYup = (
  options: Partial<BuyBoxMaxRangeOptions> = {},
) => {
  return getRangeMaxYup({
    minFieldName: 'listingPriceMin',
    ...options,
  });
};

export const getMinBedsYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxBedsYup = (options: Partial<BuyBoxMaxRangeOptions> = {}) => {
  return getRangeMaxYup({
    // maxValue: 99, // 99 bedrooms
    minFieldName: 'bedsMin',
    ...options,
  });
};

const HALF_INCREMENT_MESSAGE =
  'Must be whole number with optional .5 increment.';

export const getMinBathYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options).test(
    'halfIncrementMinBath',
    HALF_INCREMENT_MESSAGE,
    wholeNumberHalfIncrement,
  );
};

export const getMaxBathYup = (options: Partial<BuyBoxMaxRangeOptions> = {}) => {
  return getRangeMaxYup({
    minFieldName: 'bathsMin',
    ...options,
  }).test(
    'halfIncrementMaxBath',
    HALF_INCREMENT_MESSAGE,
    wholeNumberHalfIncrement,
  );
};

export const getMinGlaYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxGlaYup = (options: Partial<BuyBoxMaxRangeOptions> = {}) => {
  return getRangeMaxYup({
    // maxValue: 50000, // 50,000 square feet
    minFieldName: 'glaMin',
    ...options,
  });
};

export const getMinLotSizeYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxLotSizeYup = (
  options: Partial<BuyBoxMaxRangeOptions> = {},
) => {
  return getRangeMaxYup({
    // maxValue: 1000000, // 1 million square feet
    minFieldName: 'lotSizeMin',
    ...options,
  });
};

export const getMinYearBuiltYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup({
    minValue: 1700, // year 1700
    shouldFormatNumber: false,
    ...options,
  });
};

export const getMaxYearBuiltYup = (
  options: Partial<BuyBoxMaxRangeOptions> = {},
) => {
  return getRangeMaxYup({
    maxValue: 2050, // year 2050
    minFieldName: 'yearBuiltMin',
    shouldFormatNumber: false,
    ...options,
  });
};

export const getMinNumStoriesYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxNumStoriesYup = (
  options: Partial<BuyBoxMaxRangeOptions> = {},
) => {
  return getRangeMaxYup({
    // maxValue: 99, // 99 stories
    minFieldName: 'numStoriesMin',
    ...options,
  });
};

export const getMinGarageSpacesYup = (options: BuyBoxMinRangeOptions = {}) => {
  return getRangeMinYup(options);
};

export const getMaxGarageSpacesYup = (
  options: Partial<BuyBoxMaxRangeOptions> = {},
) => {
  return getRangeMaxYup({
    // maxValue: 99, // 99 garage spaces
    minFieldName: 'garageSpacesMin',
    ...options,
  });
};

export const getPropertyTypeYup = () => {
  return yup.array().min(1, 'At least one Property Type is required');
};

export const getCustomZipcodesYup = () => {
  return yup
    .array()
    .test(
      'custom-zipcodes-required',
      'Please select a zipcode',
      function (value) {
        const { geoBoundary, msaId } = this.parent;
        const hasCustomZipcodes = value && value.length > 0;
        const hasMsaId = msaId && msaId.trim().length > 0;

        if (
          (geoBoundary === GeoBoundaryCity.value ||
            geoBoundary === GeoBoundaryZipcode.value ||
            !hasMsaId) &&
          !hasCustomZipcodes
        ) {
          return this.createError({
            message: 'This field is required',
          });
        }

        return true;
      },
    );
};

export const getSelectedZipcodesYup = () => {
  return yup
    .array()
    .nullable()
    .test(
      'mutually-exclusive-zipcodes',
      'Either custom zipcodes or selected zipcodes must be provided, but not both',
      function (value) {
        const { geoBoundary, msaId } = this.parent;
        const hasSelectedZipcodes = value && value.length > 0;
        const hasMsaId = msaId && msaId.trim().length > 0;
        const isMsaCreate = geoBoundary === 'MSA';
        const isMsaEdit = !geoBoundary && hasMsaId;

        // This is an MSA boundary
        if (!hasSelectedZipcodes && (isMsaCreate || isMsaEdit)) {
          return this.createError({
            message: 'At least one Zip Code is required',
          });
        }

        return true;
      },
    );
};

export const buyBoxValidationSchema = yup.object().shape({
  name: getNameYup(),
  msaId: getMsaYup(),
  geoBoundary: yup.string().optional(),
  listingPriceMin: getMinListingPriceYup(),
  listingPriceMax: getMaxListingPriceYup(),
  bedsMin: getMinBedsYup(),
  bedsMax: getMaxBedsYup(),
  bathsMin: getMinBathYup(),
  bathsMax: getMaxBathYup(),
  glaMin: getMinGlaYup(),
  glaMax: getMaxGlaYup(),
  lotSizeMin: getMinLotSizeYup(),
  lotSizeMax: getMaxLotSizeYup(),
  yearBuiltMin: getMinYearBuiltYup(),
  yearBuiltMax: getMaxYearBuiltYup(),
  numStoriesMin: getMinNumStoriesYup(),
  numStoriesMax: getMaxNumStoriesYup(),
  garageSpacesMin: getMinGarageSpacesYup(),
  garageSpacesMax: getMaxGarageSpacesYup(),
  propertyType: getPropertyTypeYup(),
  customZipcodes: getCustomZipcodesYup(),
  selectedZipcodes: getSelectedZipcodesYup(),
});

export const DEFAULT_FORM_VALUES: DefaultValues<BuyBoxFormData> = {
  name: null,
  msaId: '',
  geoBoundary: undefined,
  placeId: undefined,
  listingPriceMin: null,
  listingPriceMax: null,
  bedsMin: null,
  bedsMax: null,
  bathsMin: null,
  bathsMax: null,
  glaMin: null,
  glaMax: null,
  lotSizeMin: null,
  lotSizeMax: null,
  yearBuiltMin: null,
  yearBuiltMax: null,
  numStoriesMin: null,
  numStoriesMax: null,
  garageSpacesMin: null,
  garageSpacesMax: null,
  propertyType: Object.values(LEAD_FEED_PROPERTY_TYPE),
  excludePool: true,
  excludeRentalListings: true,
  excludeSeptic: true,
  customZipcodes: [],
  selectedZipcodes: undefined,
  unSelectedZipcodes: undefined,
  historicalDetection: false,
  shouldSaveAsTemplate: false,
  crimePercentileMin: null,
  crimePercentileMax: null,
  primarySchoolPercentileMin: null,
  primarySchoolPercentileMax: null,
  middleSchoolPercentileMin: null,
  middleSchoolPercentileMax: null,
  highSchoolPercentileMin: null,
  highSchoolPercentileMax: null,
  salePriceMin: null,
  salePriceMax: null,
  rentalYieldMin: null,
  rentalYieldMax: null,
  saleDate: null,
  conditionClass: null,
  hasBasement: null,
  listingStatus: [],
};

/**
 * Converts PlatformSearch's SpatialFiltersInput into form default values for
 * the SaveSearchDialog buy box form. Geo/identity fields (name, msaId, etc.)
 * are intentionally left at their defaults — they are filled by the dialog UI.
 *
 * NOTE: When pool/privateSewer is undefined (no filter set), the buy box
 * defaults to excludePool/excludeSeptic = true, matching CreateBuyBoxDialog
 * defaults. This is intentional.
 */
export const convertSpatialFiltersToFormData = (
  spatialFilters: SpatialFiltersInput,
): DefaultValues<BuyBoxFormData> => {
  // getBuyBoxFiltersFromSpatial handles the range and property-type mappings,
  // but only handles pool === false. We patch the missing cases here.
  const partial = getBuyBoxFiltersFromSpatial(spatialFilters);

  if (Array.isArray(spatialFilters.pool)) {
    partial.excludePool = true;
  } else if (spatialFilters.pool === true) {
    partial.excludePool = false;
  }

  if (Array.isArray(spatialFilters.privateSewer)) {
    partial.excludeSeptic = true;
  } else if (spatialFilters.privateSewer === true) {
    partial.excludeSeptic = false;
  }

  return {
    ...DEFAULT_FORM_VALUES,
    ...partial,
    // getBuyBoxFiltersFromSpatial returns undefined when propertyTypeEnum is null,
    // which would overwrite the default "all types selected". Fall back explicitly.
    propertyType: partial.propertyType ?? DEFAULT_FORM_VALUES.propertyType,
  };
};

const getSetFilter = <SetValueType>(
  setValues: SetValueType[],
  numberOfOptions: number,
): SetFilterOrNull<SetValueType> => {
  if (numberOfOptions) {
    // if all options are checked, set filter to null since all options selected has the same effect as not filtering
    if (setValues.length === numberOfOptions) {
      return null;
    }
  }

  return {
    set: setValues,
  };
};

const POOL_EXCLUDE_VALUE = 1;
const SEWER_EXCLUDE_VALUE = 'septic';
const RENTAL_EXCLUDE_VALUE = 1;

const getExcludeFilter = <ExcludeValueType>(
  excludeChecked: boolean | undefined,
  excludeValue: ExcludeValueType,
): ExcludeFilterOrNull<ExcludeValueType> => {
  if (excludeChecked) {
    return {
      set: [excludeValue],
    };
  }
  return null;
};

interface BulkEditRangeConfig {
  rangeFilterName: keyof BulkEditRangeFilters;
  minActive: keyof BulkEditRangeActiveFormData;
  minField: keyof BuyBoxRangeFormData;
  maxActive: keyof BulkEditRangeActiveFormData;
  maxField: keyof BuyBoxRangeFormData;
}

const getBulkEditRangeFilter = (
  formData: BulkEditBuyBoxFormData,
  config: BulkEditRangeConfig,
): BulkEditRangeFilterOrNull => {
  const minActive = formData[config.minActive];
  const minValue = formData[config.minField];
  const maxActive = formData[config.maxActive];
  const maxValue = formData[config.maxField];

  // we are (or should be) setting these to a default of null (with react-hook-form defaultValues),
  // but react-hook-form won't include fields that aren't part of the form (even if they have a default value)
  const minValueNoUndefined = minValue === undefined ? null : minValue;
  const maxValueNoUndefined = maxValue === undefined ? null : maxValue;

  // if both min and max are null, the whole filter should be null
  if (minActive && maxActive) {
    if (minValueNoUndefined === null && maxValueNoUndefined === null) {
      return null;
    }
  }

  const rangeFilter: BulkEditRangeFilter = {};
  if (minActive) {
    rangeFilter.min = minValueNoUndefined;
  }
  if (maxActive) {
    rangeFilter.max = maxValueNoUndefined;
  }

  return rangeFilter;
};

const getFiltersFromFormData = (formData: BuyBoxFormData): Filters => {
  return {
    propertyType: getSetFilter<LeadFeedPropertyType>(
      formData.propertyType,
      Object.keys(LEAD_FEED_PROPERTY_TYPE).length,
    ),
    pool: getExcludeFilter(formData.excludePool, POOL_EXCLUDE_VALUE),
    sewer: getExcludeFilter(formData.excludeSeptic, SEWER_EXCLUDE_VALUE),
    rental: getExcludeFilter(
      formData.excludeRentalListings,
      RENTAL_EXCLUDE_VALUE,
    ),
    yearBuilt: getBuyBoxRangeFilter(
      formData.yearBuiltMin,
      formData.yearBuiltMax,
    ),
    listPrice: getBuyBoxRangeFilter(
      formData.listingPriceMin,
      formData.listingPriceMax,
    ),
    beds: getBuyBoxRangeFilter(formData.bedsMin, formData.bedsMax),
    baths: getBuyBoxRangeFilter(formData.bathsMin, formData.bathsMax),
    sqft: getBuyBoxRangeFilter(formData.glaMin, formData.glaMax),
    lotArea: getBuyBoxRangeFilter(formData.lotSizeMin, formData.lotSizeMax),
    noOfStories: getBuyBoxRangeFilter(
      formData.numStoriesMin,
      formData.numStoriesMax,
    ),
    parkingGarageCount: getBuyBoxRangeFilter(
      formData.garageSpacesMin,
      formData.garageSpacesMax,
    ),
    crimeCountyPercentile: getBuyBoxRangeFilter(
      formData.crimePercentileMin,
      formData.crimePercentileMax,
    ),
    bestPrimarySchoolPercentile: getBuyBoxRangeFilter(
      formData.primarySchoolPercentileMin,
      formData.primarySchoolPercentileMax,
    ),
    bestMiddleSchoolPercentile: getBuyBoxRangeFilter(
      formData.middleSchoolPercentileMin,
      formData.middleSchoolPercentileMax,
    ),
    bestHighSchoolPercentile: getBuyBoxRangeFilter(
      formData.highSchoolPercentileMin,
      formData.highSchoolPercentileMax,
    ),
    salePrice: getBuyBoxRangeFilter(
      formData.salePriceMin,
      formData.salePriceMax,
    ),
    rentalYield: getBuyBoxRangeFilter(
      formData.rentalYieldMin != null ? formData.rentalYieldMin / 100 : null,
      formData.rentalYieldMax != null ? formData.rentalYieldMax / 100 : null,
    ),
    saleDate: formData.saleDate ?? null,
    conditionClass:
      formData.conditionClass != null
        ? { set: [formData.conditionClass] }
        : null,
    basement: formData.hasBasement ?? null,
    mlsState: formData.listingStatus?.length
      ? { set: formData.listingStatus }
      : null,
    isDisabled: false,
  };
};

export const createFormToNewBuyBox = (formData: BuyBoxFormData): NewBuyBox => {
  let newBuyBox: NewBuyBox;
  if (
    formData.geoBoundary === GeoBoundaryCity.value ||
    formData.geoBoundary === GeoBoundaryZipcode.value
  ) {
    newBuyBox = {
      enabled: true,
      name: formData.name ?? '',
      msaId: null,
      customZipcodes: formData.customZipcodes,
      defaults: getFiltersFromFormData(formData),
      disabledZipcodes: [],
      historicalDetectionAfterCreated: formData.historicalDetection,
      saveAsDefaultTemplate: formData.shouldSaveAsTemplate,
    };
  } else {
    // GeoBoundaryMSA
    newBuyBox = {
      enabled: true,
      name: formData.name ?? '',
      msaId: formData.msaId,
      customZipcodes: [],
      defaults: getFiltersFromFormData(formData),
      disabledZipcodes: formData.unSelectedZipcodes ?? [],
      historicalDetectionAfterCreated: formData.historicalDetection,
      saveAsDefaultTemplate: formData.shouldSaveAsTemplate,
    };
  }
  return newBuyBox;
};

export const editFormToPatchBuyBox = (
  buyBox: BuyBoxPatch,
  formData: BuyBoxFormData,
): BuyBoxPatch => {
  return {
    id: buyBox.id,
    enabled: buyBox.enabled,
    name: formData.name ?? undefined,
    defaults: getFiltersFromFormData(formData),
    disabledZipcodes: formData.unSelectedZipcodes ?? undefined,
    historicalDetectionAfterCreated: formData.historicalDetection,
    customZipcodes: formData.customZipcodes,
  };
};

const getSetFromSetFilter = <SetValueType>(
  setFilter: SetFilterOrNull<SetValueType>,
  allValues: SetValueType[],
): SetValueType[] => {
  if (setFilter === null) {
    // null has the same effect as all values selected, but we display all values selected on the frontend
    return [...allValues];
  }
  return setFilter.set;
};

const getDefaultValuesFromFilters = (
  filters: Filters,
): DefaultValues<BuyBoxFormData> => {
  return {
    listingPriceMin: getMinFromRangeFilter(filters.listPrice),
    listingPriceMax: getMaxFromRangeFilter(filters.listPrice),
    bedsMin: getMinFromRangeFilter(filters.beds),
    bedsMax: getMaxFromRangeFilter(filters.beds),
    bathsMin: getMinFromRangeFilter(filters.baths),
    bathsMax: getMaxFromRangeFilter(filters.baths),
    glaMin: getMinFromRangeFilter(filters.sqft),
    glaMax: getMaxFromRangeFilter(filters.sqft),
    lotSizeMin: getMinFromRangeFilter(filters.lotArea),
    lotSizeMax: getMaxFromRangeFilter(filters.lotArea),
    yearBuiltMin: getMinFromRangeFilter(filters.yearBuilt),
    yearBuiltMax: getMaxFromRangeFilter(filters.yearBuilt),
    numStoriesMin: getMinFromRangeFilter(filters.noOfStories),
    numStoriesMax: getMaxFromRangeFilter(filters.noOfStories),
    garageSpacesMin: getMinFromRangeFilter(filters.parkingGarageCount),
    garageSpacesMax: getMaxFromRangeFilter(filters.parkingGarageCount),
    propertyType: getSetFromSetFilter(
      filters.propertyType,
      Object.values(LEAD_FEED_PROPERTY_TYPE),
    ),
    excludePool: getIsExcludedFromexcludeFilter(filters.pool),
    excludeRentalListings: getIsExcludedFromexcludeFilter(filters.rental),
    excludeSeptic: getIsExcludedFromexcludeFilter(filters.sewer),
    crimePercentileMin: getMinFromRangeFilter(filters.crimeCountyPercentile),
    crimePercentileMax: getMaxFromRangeFilter(filters.crimeCountyPercentile),
    primarySchoolPercentileMin: getMinFromRangeFilter(
      filters.bestPrimarySchoolPercentile,
    ),
    primarySchoolPercentileMax: getMaxFromRangeFilter(
      filters.bestPrimarySchoolPercentile,
    ),
    middleSchoolPercentileMin: getMinFromRangeFilter(
      filters.bestMiddleSchoolPercentile,
    ),
    middleSchoolPercentileMax: getMaxFromRangeFilter(
      filters.bestMiddleSchoolPercentile,
    ),
    highSchoolPercentileMin: getMinFromRangeFilter(
      filters.bestHighSchoolPercentile,
    ),
    highSchoolPercentileMax: getMaxFromRangeFilter(
      filters.bestHighSchoolPercentile,
    ),
    salePriceMin: getMinFromRangeFilter(filters.salePrice),
    salePriceMax: getMaxFromRangeFilter(filters.salePrice),
    rentalYieldMin:
      filters.rentalYield != null
        ? (getMinFromRangeFilter(filters.rentalYield) ?? null) != null
          ? getMinFromRangeFilter(filters.rentalYield)! * 100
          : null
        : null,
    rentalYieldMax:
      filters.rentalYield != null
        ? (getMaxFromRangeFilter(filters.rentalYield) ?? null) != null
          ? getMaxFromRangeFilter(filters.rentalYield)! * 100
          : null
        : null,
    saleDate: filters.saleDate ?? null,
    conditionClass: filters.conditionClass?.set[0] ?? null,
    hasBasement: filters.basement ?? null,
    listingStatus: filters.mlsState?.set ?? [],
  };
};

export const buyBoxToFormDefaultValues = (
  buyBox: BuyBox,
): DefaultValues<BuyBoxFormData> => {
  return {
    name: buyBox.name,
    msaId: buyBox.msaId,
    geoBoundary: buyBox.geoBoundary,
    placeId: buyBox.placeId,
    customZipcodes: buyBox.customZipcodes ?? [],
    selectedZipcodes:
      (buyBox.customZipcodes?.length ?? 0) > 0 ? [] : buyBox.enabledZipcodes,
    unSelectedZipcodes: buyBox.disabledZipcodes,
    ...getDefaultValuesFromFilters(buyBox.defaults),
  };
};

export const templateToFormDefaultValues = (
  template: Template,
): DefaultValues<BuyBoxFormData> => {
  return {
    templateId: template.id,
    shouldSaveAsTemplate: false,
    historicalDetection: true,
    ...getDefaultValuesFromFilters(template.template),
  };
};

export const buyBoxListToDefaultValues = (
  buyBoxList: BuyBoxListItem[],
): DefaultValues<BulkEditBuyBoxFormData> => {
  return {
    collectionIds: buyBoxList.map((buyBoxListItem) => buyBoxListItem.id),
  };
};

export const BULK_EDIT_FIELD_GROUPS: BulkEditRangeConfig[] = [
  {
    rangeFilterName: 'yearBuilt',
    minActive: 'yearBuiltMinActive',
    minField: 'yearBuiltMin',
    maxActive: 'yearBuiltMaxActive',
    maxField: 'yearBuiltMax',
  },
  {
    rangeFilterName: 'listPrice',
    minActive: 'listingPriceMinActive',
    minField: 'listingPriceMin',
    maxActive: 'listingPriceMaxActive',
    maxField: 'listingPriceMax',
  },
  {
    rangeFilterName: 'beds',
    minActive: 'bedsMinActive',
    minField: 'bedsMin',
    maxActive: 'bedsMaxActive',
    maxField: 'bedsMax',
  },
  {
    rangeFilterName: 'baths',
    minActive: 'bathsMinActive',
    minField: 'bathsMin',
    maxActive: 'bathsMaxActive',
    maxField: 'bathsMax',
  },
  {
    rangeFilterName: 'baths',
    minActive: 'bathsMinActive',
    minField: 'bathsMin',
    maxActive: 'bathsMaxActive',
    maxField: 'bathsMax',
  },
  {
    rangeFilterName: 'sqft',
    minActive: 'glaMinActive',
    minField: 'glaMin',
    maxActive: 'glaMaxActive',
    maxField: 'glaMax',
  },
  {
    rangeFilterName: 'lotArea',
    minActive: 'lotSizeMinActive',
    minField: 'lotSizeMin',
    maxActive: 'lotSizeMaxActive',
    maxField: 'lotSizeMax',
  },
  {
    rangeFilterName: 'noOfStories',
    minActive: 'numStoriesMinActive',
    minField: 'numStoriesMin',
    maxActive: 'numStoriesMaxActive',
    maxField: 'numStoriesMax',
  },
  {
    rangeFilterName: 'parkingGarageCount',
    minActive: 'garageSpacesMinActive',
    minField: 'garageSpacesMin',
    maxActive: 'garageSpacesMaxActive',
    maxField: 'garageSpacesMax',
  },
];

export const BULK_EDIT_FIELD_GROUP_MAP = BULK_EDIT_FIELD_GROUPS.reduce(
  (accum, config) => {
    const { rangeFilterName, ...restConfig } = config;
    accum[rangeFilterName] = restConfig;
    return accum;
  },

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  {} as Record<
    keyof RangeFilters,
    Omit<BulkEditRangeConfig, 'rangeFilterName'>
  >,
);

export const bulkEditFormToBulkEditPatch = (
  formData: BulkEditBuyBoxFormData,
): BulkEditPatch => {
  return {
    ids: formData.collectionIds,
    default: BULK_EDIT_FIELD_GROUPS.reduce<Partial<BulkEditRangeFilters>>(
      (accum, config) => {
        // if either are active, generate the bulk range
        if (formData[config.minActive] || formData[config.maxActive]) {
          accum[config.rangeFilterName] = getBulkEditRangeFilter(
            formData,
            config,
          );
        }
        return accum;
      },
      {},
    ),
  };
};
